/**
 * One setting, rendered from its declared type (f2.1, design D17).
 *
 *   STRING   text input
 *   INTEGER  number input, carrying `min`/`max` when the row declares them
 *   BOOLEAN  checkbox in the prototype's `check` / `ck-body` shape, `span-2`
 *   ENUM     select whose options are `allowed_values`, in order
 *   unknown  the value, read-only, with a note -- a newer backend must never
 *            blank the screen
 *
 * No per-key branch, no hard-coded label and no hard-coded option list: the
 * row's metadata is the whole specification.
 */

import { AlertCircle, CircleDot, Lock } from 'lucide-react';

import type { Setting } from '@/api/client';

import { displayedValue, isEditable, optionLabel, storedLabel, type Draft } from './settingsForm';

interface SettingFieldProps {
  setting: Setting;
  draft: Draft;
  changed: boolean;
  problem: string | null;
  disabled: boolean;
  onChange: (key: string, value: string | boolean) => void;
}

function fieldId(key: string): string {
  return `setting-${key.replace(/[^a-z0-9_-]/gi, '-')}`;
}

function Problem({ id, message }: { id: string; message: string }) {
  return (
    <span className="field-error" id={id} role="alert">
      <AlertCircle aria-hidden="true" /> {message}
    </span>
  );
}

function WasNote({ setting }: { setting: Setting }) {
  return (
    <span className="field-was">
      <CircleDot aria-hidden="true" /> Modified · was <strong>{storedLabel(setting)}</strong>
    </span>
  );
}

export function SettingField({
  setting,
  draft,
  changed,
  problem,
  disabled,
  onChange,
}: SettingFieldProps) {
  const id = fieldId(setting.key);
  const hintId = `${id}-hint`;
  const problemId = `${id}-problem`;
  const describedBy = problem ? `${hintId} ${problemId}` : hintId;
  const stateClass = `${changed ? ' is-dirty' : ''}${problem ? ' has-problem' : ''}`;

  if (!isEditable(setting)) {
    return (
      <div className="field is-readonly">
        <label htmlFor={id}>{setting.label}</label>
        <input
          id={id}
          type="text"
          value={JSON.stringify(setting.value)}
          readOnly
          aria-describedby={hintId}
        />
        <span className="hint" id={hintId}>
          {setting.description}
        </span>
        <span className="field-note">
          <Lock aria-hidden="true" /> This setting cannot be edited in this version of Atlas.
        </span>
      </div>
    );
  }

  const value = displayedValue(setting, draft);

  if (setting.value_type === 'BOOLEAN') {
    return (
      <div className={`field span-2${stateClass}`}>
        <label className="check" htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            checked={value === true}
            disabled={disabled}
            aria-describedby={describedBy}
            onChange={(event) => onChange(setting.key, event.target.checked)}
          />
          <span className="ck-body">
            {setting.label}
            <span className="hint" id={hintId}>
              {setting.description}
            </span>
            {changed && <WasNote setting={setting} />}
          </span>
        </label>
        {problem && <Problem id={problemId} message={problem} />}
      </div>
    );
  }

  const control =
    setting.value_type === 'ENUM' ? (
      <select
        id={id}
        value={String(value)}
        disabled={disabled}
        className={problem ? 'has-error' : undefined}
        aria-invalid={problem ? true : undefined}
        aria-describedby={describedBy}
        onChange={(event) => onChange(setting.key, event.target.value)}
      >
        {(setting.allowed_values ?? []).map((option) => (
          <option key={option} value={option}>
            {optionLabel(option)}
          </option>
        ))}
      </select>
    ) : (
      <input
        id={id}
        type={setting.value_type === 'INTEGER' ? 'number' : 'text'}
        inputMode={setting.value_type === 'INTEGER' ? 'numeric' : undefined}
        step={setting.value_type === 'INTEGER' ? 1 : undefined}
        min={setting.min_value ?? undefined}
        max={setting.max_value ?? undefined}
        value={String(value)}
        disabled={disabled}
        className={problem ? 'has-error' : undefined}
        aria-invalid={problem ? true : undefined}
        aria-describedby={describedBy}
        onChange={(event) => onChange(setting.key, event.target.value)}
      />
    );

  return (
    <div className={`field${stateClass}`}>
      <label htmlFor={id}>{setting.label}</label>
      {control}
      <span className="hint" id={hintId}>
        {setting.description}
      </span>
      {changed && !problem && <WasNote setting={setting} />}
      {problem && <Problem id={problemId} message={problem} />}
    </div>
  );
}
